import { useState, useRef, useLayoutEffect, useEffect } from 'react'
import { createPortal } from 'react-dom'
import positionWideTooltip from '../../helpers/tooltip/positionWideTooltip'

/**
 * Parses a CSS px string (e.g. '-60px') to a number. Empty input counts as 0.
 * @param {string | undefined} value
 * @returns {number}
 */
const parsePx = (value) => value ? parseFloat(value) : 0

/** Ruimte die een brede tooltip aan de vensterrand vrijhoudt. */
const VIEWPORT_MARGIN = 8

/**
 * Stand waarin de brede tooltip al wel gerenderd is maar nog niet gepositioneerd.
 * Hij moet eerst in de DOM staan om zijn hoogte te kunnen meten; tot dat gebeurd
 * is, is hij onzichtbaar. Er stond hier eerder een vaste schatting van 180px,
 * die 21px te laag was — zie positionWideTooltip en docs/inbox.md #150.
 */
const MEASURING_STYLE = { position: 'fixed', top: 0, left: 0, visibility: 'hidden' }

/**
 * Hover tooltip. Rendered in a portal with fixed positioning so it can never
 * be clipped by scroll containers or covered by sticky table headers — the
 * px offsets keep their original meaning relative to the trigger container.
 *
 * ALLEEN MUIS: er zijn geen touch- of focus-handlers, dus op een telefoon
 * verschijnt deze tooltip nooit. Zet er daarom niets in dat nergens anders op
 * het scherm staat — `src/pages/mobile/` hergebruikt desktopcomponenten.
 * AI-CONTEXT: See docs/pitfalls.md#ui-hover-only-affordance-dies-on-mobile
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Trigger element(s) the tooltip is anchored to
 * @param {string} props.content - Tooltip text
 * @param {string} [props.width] - CSS width of the trigger container
 * @param {string} [props.height] - CSS height of the trigger container
 * @param {string} [props.top] - Vertical px offset from the trigger container top; omitted = just below the trigger
 * @param {string} [props.placeholder] - Fallback text shown when content is an empty string
 * @param {string} [props.right] - Px offset of the tooltip right edge from the trigger container right edge
 * @param {string} [props.left] - Horizontal px offset from the trigger container left
 * @param {string} [props.maxWidth] - Px breedte waarop de tooltip mag uitlopen, los van de breedte van de trigger; voor lange tekst
 * @param {number} [props.delay] - Milliseconden dat de muis moet blijven staan voor de tooltip verschijnt; 0 of weggelaten = meteen
 */
const Tooltip = ({children, content, width, height, top, placeholder, right, left, maxWidth, delay}) => {
    const [tooltipStyle, setTooltipStyle] = useState(/** @type {React.CSSProperties | null} */ (null));
    /** @type {[{top: number, bottom: number, left: number} | null, Function]} */
    const [wideTrigger, setWideTrigger] = useState(null);
    /** @type {React.RefObject<HTMLDivElement>} */
    const containerRef = useRef(null);
    /** @type {React.RefObject<HTMLDivElement>} */
    const tooltipRef = useRef(null);
    /** @type {React.MutableRefObject<ReturnType<typeof setTimeout> | null>} */
    const timerRef = useRef(null);

    const openTooltip = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Lange tekst (de inhoud van een notitie) past niet in de breedte van de
      // trigger en is meerdere regels hoog. Die hoogte valt niet te schatten,
      // dus die tooltip gaat in twee stappen: eerst onzichtbaar renderen om te
      // meten, daarna pas positioneren. Zie het effect hieronder.
      if (maxWidth && !right) {
        setWideTrigger({ top: rect.top, bottom: rect.bottom, left: rect.left });
        setTooltipStyle(null);
        return;
      }

      /** @type {React.CSSProperties} */
      const style = {
        // Inline because the coordinates below are viewport-based and only
        // valid with fixed positioning.
        position: 'fixed',
        top: top ? rect.top + parsePx(top) : rect.bottom,
        left: rect.left + parsePx(left),
      };

      if (right) {
        // Stretch between the left edge and the offset right edge, matching
        // how the old absolute positioning combined `left: 0` with `right`.
        style.right = window.innerWidth - rect.right + parsePx(right);
      } else {
        // Keep wrapping close to the old behavior, where the trigger
        // container width capped the tooltip, but stay readable on narrow
        // triggers such as table icons.
        style.maxWidth = Math.max(rect.width - parsePx(left), 120);
      }

      setTooltipStyle(style);
    };

    /**
     * Start de tooltip, eventueel pas na een wachttijd.
     *
     * Een lopende teller wordt NIET opnieuw gestart: `onMouseOver` vuurt bij
     * elke muisbeweging binnen de trigger, en resetten zou betekenen dat de
     * tooltip nooit verschijnt zolang iemand beweegt — precies wat je doet als
     * je een afgekapte tekst probeert te lezen.
     */
    const showTooltip = () => {
      if (!delay) {
        openTooltip();
        return;
      }
      if (timerRef.current !== null) return;

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        // Pas op dit moment de positie meten: de pagina kan intussen gescrold zijn.
        openTooltip();
      }, delay);
    };

    const hideTooltip = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setTooltipStyle(null);
      setWideTrigger(null);
    };

    // Verdwijnt de trigger terwijl de teller loopt (een rij die wegvalt door een
    // filter, een venster dat sluit), dan mag die teller niet blijven staan.
    useEffect(() => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    }, []);

    // Tweede stap voor de brede tooltip: hij staat nu onzichtbaar in de DOM, dus
    // zijn echte hoogte is te meten. Pas daarna is te bepalen of hij onder of
    // boven de trigger past.
    useLayoutEffect(() => {
      if (!wideTrigger || !tooltipRef.current) return;

      setTooltipStyle({
        position: 'fixed',
        ...positionWideTooltip({
          trigger: wideTrigger,
          tooltipHeight: tooltipRef.current.offsetHeight,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
          maxWidth: parsePx(maxWidth),
          left: parsePx(left),
          margin: VIEWPORT_MARGIN,
        }),
      });
    }, [wideTrigger, maxWidth, left, content]);

    const currentContent = () => {
      if(content === ""){
        return placeholder
      } else {
        return content
      }
    }

    return (
      <div className="tooltip-container" style={{width: width, height: height}} ref={containerRef}>
        <div
          className="tooltip-trigger"
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onMouseOver={showTooltip}
        >
          {children}
        </div>
        {(tooltipStyle || wideTrigger) && createPortal(
          <div
            ref={tooltipRef}
            className="tooltip"
            style={tooltipStyle || { ...MEASURING_STYLE, maxWidth: parsePx(maxWidth) }}
          >
            {currentContent()}
          </div>,
          document.body
        )}
      </div>
    );
}

export default Tooltip
